import { chakra } from '@chakra-ui/react';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { controlSize, selectTriggerActive, selectTriggerBase, type ControlSize } from './_shared';

export interface SelectMenuOption {
  value: string;
  label: string;
  /** Optional secondary line under the label (e.g. a hint). */
  hint?: ReactNode;
  disabled?: boolean;
  /** Optional test id applied to the rendered option row. */
  'data-testid'?: string;
}

const Anchor = chakra('div', { base: { position: 'relative', display: 'inline-flex', minWidth: 0 } });

const Trigger = chakra('button', {
  base: {
    ...selectTriggerBase,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '2',
    textAlign: 'left',
    _focusVisible: selectTriggerActive,
    '&[aria-expanded="true"]': selectTriggerActive,
  },
});

const Listbox = chakra('ul', {
  base: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    zIndex: 'dropdown',
    listStyleType: 'none',
    margin: 0,
    padding: '1',
    minWidth: 'full',
    maxHeight: '18rem',
    overflowY: 'auto',
    bg: 'surface.raised',
    borderWidth: '1px',
    borderColor: 'border.default',
    borderRadius: 'lg',
    boxShadow: 'lg',
  },
});

const Option = chakra('li', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '2',
    px: '2.5',
    py: '2',
    borderRadius: 'md',
    cursor: 'pointer',
    color: 'text.primary',
    fontSize: 'md',
    lineHeight: 'snug',
    _hover: { bg: 'action.secondary' },
    '&[data-active="true"]': { bg: 'action.secondary' },
    '&[aria-selected="true"]': { fontWeight: 'semibold' },
    '&[aria-disabled="true"]': { opacity: 0.5, cursor: 'not-allowed', _hover: { bg: 'transparent' } },
  },
});

export interface SelectMenuProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectMenuOption[];
  /** Accessible label for the control. */
  label: string;
  /** Label shown when `value` matches no option (e.g. an "all" pseudo-option). */
  placeholder?: string;
  size?: ControlSize;
  minWidth?: string;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * A custom listbox select (button trigger + popup `role="listbox"` of `role="option"`
 * rows). Unlike a native `<select>`, its options are real DOM nodes when open — so they
 * are queryable/clickable by role, keyboard-navigable (↑/↓/Home/End, Enter, Escape,
 * type-ahead), and fully themeable. Used for filter controls (agent filter, etc.).
 */
export function SelectMenu({
  value,
  onChange,
  options,
  label,
  placeholder = 'Select…',
  size = 'md',
  minWidth = '12rem',
  disabled,
  ...rest
}: SelectMenuProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();

  const selected = options.find((o) => o.value === value);
  const selectedIndex = options.findIndex((o) => o.value === value);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setActiveIndex(-1);
    if (refocus) triggerRef.current?.focus();
  }, []);

  const openMenu = useCallback(() => {
    setOpen(true);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [selectedIndex]);

  const commit = useCallback(
    (idx: number) => {
      const opt = options[idx];
      if (!opt || opt.disabled) return;
      onChange(opt.value);
      close();
    },
    [options, onChange, close],
  );

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (!anchorRef.current?.contains(e.target as Node)) close(false);
    };
    document.addEventListener('mousedown', onDocDown);
    const id = requestAnimationFrame(() => listRef.current?.focus());
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      cancelAnimationFrame(id);
    };
  }, [open, close]);

  const move = (delta: number) => {
    setActiveIndex((cur) => {
      const n = options.length;
      let next = cur;
      for (let i = 0; i < n; i++) {
        next = (next + delta + n) % n;
        if (!options[next]?.disabled) break;
      }
      return next;
    });
  };

  const onListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        move(-1);
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        commit(activeIndex);
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'Tab':
        close(false);
        break;
    }
  };

  return (
    <Anchor ref={anchorRef} minW={minWidth}>
      <Trigger
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={label}
        disabled={disabled}
        css={controlSize[size]}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            openMenu();
          }
        }}
        {...rest}
      >
        <chakra.span overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" color={selected ? 'text.primary' : 'text.muted'}>
          {selected?.label ?? placeholder}
        </chakra.span>
        <chakra.span display="inline-flex" color="text.muted" flexShrink={0}>
          <ChevronDown size={16} strokeWidth={2} aria-hidden />
        </chakra.span>
      </Trigger>
      {open && (
        <Listbox ref={listRef} id={listboxId} role="listbox" aria-label={label} tabIndex={-1} onKeyDown={onListKeyDown}>
          {options.map((opt, i) => {
            const isSelected = opt.value === value;
            return (
              <Option
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled || undefined}
                data-active={i === activeIndex || undefined}
                data-testid={opt['data-testid']}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => commit(i)}
              >
                <chakra.span display="inline-flex" boxSize="4" flexShrink={0} color="action.primary">
                  {isSelected && <Check size={16} strokeWidth={2.5} aria-hidden />}
                </chakra.span>
                <chakra.span display="flex" flexDirection="column" minW={0}>
                  <chakra.span overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {opt.label}
                  </chakra.span>
                  {opt.hint && (
                    <chakra.span fontSize="xs" color="text.muted">
                      {opt.hint}
                    </chakra.span>
                  )}
                </chakra.span>
              </Option>
            );
          })}
        </Listbox>
      )}
    </Anchor>
  );
}
